import { useState, useEffect } from 'react';
import { useWhiteLabel } from '../../context/WhiteLabelContext';
import './scss/BrandLogo.scss';

const BrandLogo = ({
  size = 'medium',
  showText = true,
  className = '',
  onClick,
  style = {},
  useIcon = false,
}) => {
  const { getLogo, getLogoDark, getLogoIcon, getAppName, shouldHideZoobbeBranding, isWhiteLabel } = useWhiteLabel();

  const appName = getAppName();
  const hideZoobbe = shouldHideZoobbeBranding();

  // Short form of the app name for the collapsed/icon rail (~60px) when no
  // logo icon is uploaded. Prefer the first word if it's short, otherwise the
  // initials — but it's still the app NAME, not a generic badge.
  // e.g. "Social Philosophy" -> "SP", "Acme" -> "Acme".
  const getShortAppName = () => {
    if (!appName) return '';
    const words = appName.trim().split(/\s+/).filter(Boolean);
    // Single short word fits as-is.
    if (words.length === 1) {
      return words[0].length <= 6 ? words[0] : words[0].slice(0, 2).toUpperCase();
    }
    // Multi-word: use initials so it fits the narrow rail.
    return words.map((w) => w[0]).join('').toUpperCase();
  };

  // Detect current theme
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  useEffect(() => {
    // Check if dark theme is active
    const checkTheme = () => {
      const htmlElement = document.documentElement;
      const isDark = htmlElement.classList.contains('dark-theme');
      setIsDarkTheme(isDark);
    };

    // Initial check
    checkTheme();

    // Watch for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // decide which asset to use
  const wantIcon = useIcon || !showText; // collapsed sidebar -> icon

  // Get the appropriate logo based on theme
  let logoSrc;
  if (wantIcon) {
    logoSrc = getLogoIcon();
  } else {
    // Use dark logo if dark theme is active and dark logo exists
    const darkLogo = getLogoDark();
    logoSrc = (isDarkTheme && darkLogo) ? darkLogo : getLogo();
  }

  // Size and mode classes
  const sizeClass = `brand-logo--${size}`;
  const modeClass = wantIcon ? 'brand-logo--icon' : '';

  // Combined class names
  const logoClasses = `brand-logo ${sizeClass} ${modeClass} ${className}`.trim();

  // If white label has custom asset, use it
  if (logoSrc) {
    return (
      <div className={logoClasses} onClick={onClick} style={style}>
        <img
          src={logoSrc}
          alt={appName}
          className="brand-logo__image"
        />
        {/* {showText && (
          <span className="brand-logo__text">{appName}</span>
        )} */}
      </div>
    );
  }

  // If white label but no custom logo:
  if (isWhiteLabel() && hideZoobbe) {
    // Collapsed/icon mode with no icon uploaded -> show a short form of the
    // app name as plain text (fits the narrow rail), never the full name.
    if (wantIcon) {
      return (
        <div className={logoClasses} onClick={onClick} style={style}>
          <span className="brand-logo__text brand-logo__text--short" title={appName}>
            {getShortAppName()}
          </span>
        </div>
      );
    }

    // Expanded mode -> show full app name.
    return (
      <div className={logoClasses} onClick={onClick} style={style}>
        <span className="brand-logo__text brand-logo__text--only" title={appName}>
          {appName}
        </span>
      </div>
    );
  }

  // Default Zoobbe logo
  return (
    <div className={logoClasses} onClick={onClick} style={style}>
      <div className="brand-logo__zoobbe">
        <span className="brand-logo__z">Z</span>
        {showText && (
          <span className="brand-logo__text">oobbe</span>
        )}
      </div>
    </div>
  );
};

export default BrandLogo;
